import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError, api } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { Textarea } from '../../components/ui/form';

/**
 * Everything `ReportRepository::SUBJECTS` accepts.
 *
 * `post` is an update on a profile. The list is the server's, not this file's -
 * a subject missing here is a report the product cannot file even though the
 * endpoint would have taken it, which is what kept account reporting invisible.
 */
export type ReportSubject = 'track' | 'comment' | 'user' | 'collection' | 'post';

export type ReportReason =
  | 'copyright'
  | 'spam'
  | 'harassment'
  | 'sexual'
  | 'violence'
  | 'other';

/**
 * Written as a person would say it, not as the enum spells it.
 *
 * The stored value is `sexual`; what somebody picking from a list needs to read
 * is a sentence about what they saw. A moderation form that makes the reporter
 * translate their experience into a taxonomy gets worse reports.
 */
const REASONS: { value: ReportReason; label: string; hint: string }[] = [
  {
    value: 'copyright',
    label: 'It uses my work without permission',
    hint: 'Someone has uploaded music you own the rights to.',
  },
  { value: 'spam', label: 'Spam or a scam', hint: 'Advertising, bulk posting, or a fake.' },
  {
    value: 'harassment',
    label: 'Harassment or hate',
    hint: 'Targeted abuse, threats, or hate towards a person or group.',
  },
  { value: 'sexual', label: 'Sexual content', hint: 'Explicit material, or content involving minors.' },
  { value: 'violence', label: 'Violence or self-harm', hint: 'Graphic violence, or encouraging harm.' },
  { value: 'other', label: 'Something else', hint: 'Tell us what is wrong below.' },
];

/**
 * Report something.
 *
 * ## The response is deliberately the same either way
 *
 * Filing a report you have already filed returns the same acknowledgement as a
 * new one. "You have already reported this" would confirm that a report exists,
 * and a report system that discloses its own contents is one an abuser can use
 * to find out whether their target has reported them.
 */
export function ReportDialog({
  subjectType,
  subjectId,
  title,
  onClose,
}: {
  subjectType: ReportSubject;
  subjectId: number;
  /** What is being reported, so the dialog can name it back. */
  title: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState('');
  const [sent, setSent] = useState(false);

  const file = useMutation({
    mutationFn: () =>
      api.post('/reports', {
        subject_type: subjectType,
        subject_id: subjectId,
        reason,
        detail: detail.trim() || null,
      }),
    onSuccess: () => setSent(true),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (reason === null) return;

    file.mutate();
  };

  if (sent) {
    return (
      <Modal title="Thanks" onClose={onClose}>
        <div className="flex flex-col gap-4" data-report-sent>
          <p className="text-sm text-soft">
            A moderator will look at this. We do not send updates on individual reports. If
            something is removed, you will see it.
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Report ${title}`} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-5" data-report-form>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm text-soft">What is wrong with it?</legend>

          {REASONS.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer gap-3 rounded-[var(--radius-control)] border px-3 py-2.5 transition-colors ${
                reason === option.value
                  ? 'border-line-lit bg-raised'
                  : 'border-line hover:border-line-strong'
              }`}
            >
              <input
                type="radio"
                name="reason"
                value={option.value}
                checked={reason === option.value}
                data-reason={option.value}
                onChange={() => setReason(option.value)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span className="min-w-0">
                <span className="block text-sm text-ink">{option.label}</span>
                <span className="block text-xs text-faint">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {/*
          A report is not a takedown notice.
          Somebody choosing this reason is the exact person the DMCA form
          exists for, and they have found the wrong door - a moderator reading
          "that's my song" has no sworn statement to act on and has to write
          back asking for one. Offered rather than forced: a report still gets
          logged, and being told to fill in a longer form instead of being
          heard at all is how people stop reporting.
        */}
        {reason === 'copyright' && (
          <Alert tone="info">
            To have it taken down you need to file a copyright notice. It asks for the sworn
            statement a moderator has to act on.{' '}
            <Link to="/dmca" data-report-to-dmca className="text-accent hover:underline">
              File one instead
            </Link>
            . This report will be read either way.
          </Alert>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-detail" className="text-sm font-medium text-soft">
            Anything else?{' '}
            <span className="font-normal text-faint">
              {reason === 'other' ? '' : 'Optional.'}
            </span>
          </label>
          <Textarea
            id="report-detail"
            className="min-h-24"
            value={detail}
            maxLength={2000}
            data-report-detail
            placeholder="Context helps a moderator decide faster."
            onChange={(event) => setDetail(event.target.value)}
          />
        </div>

        {file.isError && (
          <Alert tone="error">
            {file.error instanceof ApiError ? file.error.message : 'That could not be sent.'}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={file.isPending} disabled={reason === null}>
            Send report
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={file.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The little "report" affordance that opens the dialog.
 *
 * Understated on purpose. It has to be findable by somebody who needs it and
 * not so prominent that it reads as an invitation - a report button styled like
 * a primary action gets used as a disagree button.
 */
export function ReportButton({
  subjectType,
  subjectId,
  title,
  className = '',
}: {
  subjectType: ReportSubject;
  subjectId: number;
  title: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        data-report-button
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        className={`text-xs text-faint transition-colors hover:text-hot ${className}`}
      >
        Report
      </button>

      {open && (
        <ReportDialog
          subjectType={subjectType}
          subjectId={subjectId}
          title={title}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
